import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  FOLLOWED_TOPIC_KINDS,
  SUGGESTED_COLLECTIONS,
  type FollowedTopicKind,
} from "@connect/shared";
import { useApi } from "@/lib/api";
import { useFollows, type Follow } from "@/lib/useFollows";
import { useCollections, type Collection } from "@/lib/useCollections";
import { useTrack } from "@/lib/track";
import { Section } from "@/components/intel/Section";
import { ItemCard, ItemCardEmpty, type FeedItem } from "@/components/intel/ItemCard";
import { OpportunityCard, type Opportunity } from "@/components/opps/OpportunityCard";
import { FollowButton } from "@/components/follows/FollowButton";

interface SavedItem extends FeedItem {
  saved_at: number;
}
interface SavedOpp extends Opportunity {
  status: "saved" | "pursuing" | "applied" | "won" | "passed";
  saved_at: number;
}

const FOLLOW_LABELS: Record<FollowedTopicKind, string> = {
  opportunity_kind: "Opportunity kinds",
  category: "Topics",
  geo: "Geographies",
  entity: "Entities",
};

/**
 * Library — a personal intelligence memory built over time. Collections sit
 * at the top so the user's curated themes lead the page; saved-but-uncollected
 * items + opportunities live below; follows stay last because they shape
 * incoming intelligence, not stored intelligence.
 */
export function Library() {
  const api = useApi();
  const collections = useCollections();
  const intel = useQuery({
    queryKey: ["intelligence-saved"],
    queryFn: () => api.get<{ items: SavedItem[] }>("/intelligence/saved"),
  });
  const opps = useQuery({
    queryKey: ["opp-saved"],
    queryFn: () => api.get<{ opportunities: SavedOpp[] }>("/opportunities/saved"),
  });
  const follows = useFollows();

  const intelItems = intel.data?.items ?? [];
  const oppItems = opps.data?.opportunities ?? [];
  const pursuing = oppItems.filter((o) => o.status === "pursuing");
  const saved = oppItems.filter((o) => o.status === "saved");
  const collectionList = collections.data?.collections ?? [];

  const groupedFollows = useMemo(() => {
    const acc: Record<FollowedTopicKind, Follow[]> = {
      opportunity_kind: [],
      category: [],
      geo: [],
      entity: [],
    };
    for (const f of follows.data?.follows ?? []) acc[f.topic_kind]?.push(f);
    return acc;
  }, [follows.data]);

  const totalFollows = follows.data?.follows.length ?? 0;

  return (
    <div className="px-10 py-10 max-w-5xl">
      <header className="mb-12">
        <p className="label-eyebrow">Personal</p>
        <h1 className="mt-2 text-display-2 font-display">Library</h1>
        <p className="mt-3 text-whisper max-w-xl leading-relaxed">
          A personal archive of intelligence, opportunities, and reading you've
          kept. Build it slowly — themes you return to over weeks and months.
        </p>
      </header>

      <Section
        eyebrow="Themes"
        title="Your collections"
        detail={
          collectionList.length > 0
            ? `${collectionList.length} curated theme${collectionList.length === 1 ? "" : "s"}`
            : "Curate the threads you keep returning to."
        }
      >
        {collections.isLoading ? (
          <ItemCardEmpty label="Reading your collections…" />
        ) : collectionList.length === 0 ? (
          <SuggestedCollections />
        ) : (
          <CollectionList collections={collectionList} />
        )}
      </Section>

      <Section
        eyebrow="Saved"
        title="Intelligence"
        detail={
          intelItems.length > 0
            ? `${intelItems.length} signal${intelItems.length === 1 ? "" : "s"} kept`
            : "Bookmark any item to keep it here."
        }
      >
        {intel.isLoading ? (
          <ItemCardEmpty label="Reading your library…" />
        ) : intelItems.length === 0 ? (
          <ItemCardEmpty label="Nothing saved yet." />
        ) : (
          <div className="grid gap-4">
            {intelItems.map((it) => (
              <div key={it.id}>
                <p className="mb-2 text-[10px] uppercase tracking-[0.18em] text-muted">
                  Saved {timeAgo(it.saved_at)}
                </p>
                <ItemCard item={it} variant="full" isSaved />
              </div>
            ))}
          </div>
        )}
      </Section>

      {pursuing.length > 0 && (
        <Section
          eyebrow="Saved"
          title="Pursuing"
          detail="Opportunities you've moved into your active pipeline."
        >
          <div className="grid gap-4">
            {pursuing.map((o) => (
              <OpportunityCard key={o.id} opp={o} variant="full" />
            ))}
          </div>
        </Section>
      )}

      {saved.length > 0 && (
        <Section
          eyebrow="Saved"
          title="Opportunities"
          detail="Bookmarked, not yet pursued."
        >
          <div className="grid gap-4">
            {saved.map((o) => (
              <OpportunityCard key={o.id} opp={o} variant="full" />
            ))}
          </div>
        </Section>
      )}

      <Section
        eyebrow="Personal"
        title="Follows"
        detail={
          totalFollows > 0
            ? `${totalFollows} topic${totalFollows === 1 ? "" : "s"} shaping your feed.`
            : "Follow topics, geographies, or kinds to personalize what surfaces."
        }
      >
        {follows.isLoading ? (
          <ItemCardEmpty label="Reading your follows…" />
        ) : totalFollows === 0 ? (
          <ItemCardEmpty
            label="Nothing followed yet."
            detail="Visit any opportunity and tap + to follow its kind."
          />
        ) : (
          <div className="panel p-6 space-y-5">
            {FOLLOWED_TOPIC_KINDS.map((kind) => {
              const list = groupedFollows[kind];
              if (list.length === 0) return null;
              return (
                <div key={kind}>
                  <p className="label-eyebrow mb-2">{FOLLOW_LABELS[kind]}</p>
                  <div className="flex flex-wrap gap-2">
                    {list.map((f) => (
                      <FollowButton
                        key={`${f.topic_kind}-${f.topic_value}`}
                        topicKind={f.topic_kind}
                        topicValue={f.topic_value}
                        isFollowing
                        variant="pill"
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}

function CollectionList({ collections }: { collections: Collection[] }) {
  return (
    <ul className="panel divide-y divide-veil/40">
      {collections.map((c) => {
        // Quiet hint: only when a collection has had no activity for 14+ days.
        // Empty collections under 14d old don't carry the hint — they're just
        // new, not stale.
        const showQuiet = c.quiet_days >= 14;
        return (
          <li key={c.id}>
            <Link
              to={`/app/library/${encodeURIComponent(c.slug)}`}
              className="block px-6 py-5 hover:bg-veil/20 transition-colors group"
            >
              <div className="flex items-center justify-between gap-6">
                <div className="min-w-0">
                  <div className="font-display text-base tracking-tight group-hover:text-gold-glow transition-colors">
                    {c.name}
                  </div>
                  {c.description && (
                    <p className="mt-1 text-xs text-whisper leading-relaxed line-clamp-1">
                      {c.description}
                    </p>
                  )}
                  {showQuiet && (
                    <p className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-gold-glow/60">
                      Quiet for {c.quiet_days} days
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0 text-[10px] uppercase tracking-[0.18em] text-muted">
                  <div className="text-whisper tabular-nums">
                    {c.entry_count}
                  </div>
                  <div>{c.entry_count === 1 ? "entry" : "entries"}</div>
                </div>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function SuggestedCollections() {
  const api = useApi();
  const qc = useQueryClient();
  const track = useTrack();
  const [pending, setPending] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (name: string) => api.post("/collections", { name }),
    onMutate: (name) => setPending(name),
    onSettled: () => setPending(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections"] });
      track("collection.create", { source: "suggestion" });
    },
  });

  return (
    <div className="panel p-6 space-y-4">
      <p className="text-sm text-whisper leading-relaxed">
        No collections yet. These are common starting points — start one in a
        single click, or create your own from any saved item.
      </p>
      <div className="flex flex-wrap gap-2">
        {SUGGESTED_COLLECTIONS.map((s) => (
          <button
            key={s.slug}
            type="button"
            onClick={() => create.mutate(s.name)}
            disabled={pending === s.name}
            className="px-3 py-1.5 rounded-full text-xs border border-veil/60 text-whisper hover:border-gold/40 hover:text-gold-glow transition-colors disabled:opacity-40"
          >
            {pending === s.name ? "…" : `+ ${s.name}`}
          </button>
        ))}
      </div>
    </div>
  );
}

function timeAgo(ts: number): string {
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
